"use client";

import React, { useCallback, useMemo } from "react";
import { BettingOdds as BettingOddsUI } from "fansunited-sports-ui";
import type { BookmakerOdds, OddsData, MarketType } from "../types";
import { useBetslip } from "@/features/betslip/useBetslip";
import type { BetslipMarket } from "@/features/betslip/types";
import { useOddsDisplay } from "../useOddsDisplay";

interface MarketCardProps {
    /** Title for the market card */
    title: string;
    /** Bookmaker odds data for this market */
    bookmakerOdds: BookmakerOdds[];
    /** Match ID for betslip integration */
    matchId?: string;
    /** Market type for betslip integration */
    market?: MarketType;
    /** ISO 8601 kickoff timestamp — passed to addOddsSelection so persisted items expire 3 h after kickoff */
    kickoffAt?: string;
}

/**
 * Renders a single market card with bookmaker odds.
 * Reusable component for displaying betting odds across different tabs.
 */
export function MarketCard({ title, bookmakerOdds, matchId, market, kickoffAt }: MarketCardProps) {
    const { addOddsSelection, canAddOdds } = useBetslip();
    const tenantOdds = useOddsDisplay();

    // Check if we should intercept odds clicks
    const shouldInterceptOdds = canAddOdds && !!matchId && !!market;

    // Handle odds click - add to betslip via provider
    const handleOddsClick = useCallback(
        (odd: OddsData) => {
            if (!shouldInterceptOdds || !market || !matchId) return;
            addOddsSelection(matchId, market as BetslipMarket, odd.label, kickoffAt);
        },
        [shouldInterceptOdds, market, matchId, addOddsSelection, kickoffAt]
    );

    // Map odds with onClick handlers if in predictions-and-odds mode
    const processedBookmakerOddsList = useMemo(() => {
        if (!shouldInterceptOdds) {
            return bookmakerOdds;
        }

        return bookmakerOdds.map((bookmakerOdds) => ({
            ...bookmakerOdds,
            odds: bookmakerOdds.odds.map((odd) => ({
                ...odd,
                onClick: () => handleOddsClick(odd),
                // Strip URL to render as button instead of link
                url: undefined,
            })),
        }));
    }, [bookmakerOdds, shouldInterceptOdds, handleOddsClick]);

    if (bookmakerOdds.length === 0) return null;

    return (
        <div className="bg-card text-card-foreground p-4">
            <h4 className="text-sm font-medium mb-3">{title}</h4>
            <div className="space-y-2">
                {processedBookmakerOddsList.map((bookie, index) => (
                    <BettingOddsUI
                        key={`${bookie.operator.name}-${index}`}
                        odds={bookie.odds}
                        operator={bookie.operator}
                        styles={{
                            container: "p-0",
                            operatorContainer: "p-0",
                            oddsContainer: "px-2 py-1.5 odds-button",
                            mode: tenantOdds.displayMode,
                            flashInterval: tenantOdds.flashInterval,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
